import { clamp } from 'lodash-es';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { fromEvent, tap } from 'rxjs';

import { useToolPlayControllerContext } from '@/component/Tool/ToolPlayController';
import { pcdManager } from '@/manager';
import {
  ArrowBack,
  ArrowForward,
  Explore,
  Latitude,
  Longitude,
  PauseCircle,
  PlayCircle,
  RightPanelClose,
  RightPanelOpen,
  Rotate360,
  SpeedLine,
  VFlex,
  VInput,
  VStack,
  VTooltip,
  cn,
  useShortcut,
  useSubscriptions,
  useUnleash,
} from '@/package';
import {
  useAuthStore,
  useIssueReportStore,
  usePcdRenderingStore,
  useTaskFrameStore,
  useToolMenuStore,
} from '@/store';

import TaskLayoutFooterPlaybar from './TaskLayoutFooterPlaybar';

const TaskLayoutFooter = () => {
  const { t } = useTranslation();
  const { getVariantPayloadValue, isEnabled } = useUnleash();
  const {
    allFrameListLength,
    isPlaybarOpen,
    isPlaying,
    pcdInterval,
    visibleOnlyMyFrame,

    $toolPlayController,

    goFirst,
    goLast,
    moveFrameSeq,
    next,
    nextMine,
    prev,
    prevMine,
    play,
    showAllFrame,
    showOnlyMyFrame,
    setPcdInterval,
    stop,
    togglePlaybar,
  } = useToolPlayControllerContext();
  const { pcdIndex } = usePcdRenderingStore();
  const { namespace, register, run, unregister } = useShortcut();
  const { toolMap, findByName, select, selectChildren } = useToolMenuStore();
  const { allFrameList, currentFrame, frameCount, metaData, selectedTask } =
    useTaskFrameStore();
  const [searchParams] = useSearchParams();
  const currentFrameSeq = currentFrame?.frameSeq ?? 1;
  const total =
    (visibleOnlyMyFrame ? frameCount.total : allFrameListLength) ?? 0;
  const { user } = useAuthStore();
  const { setPosition, setVariant, clear } = useIssueReportStore();
  const seq =
    (() => {
      if (visibleOnlyMyFrame) {
        return allFrameList
          .filter(f => f?.worker?.id === user.id)
          .findIndex(f => f.frameSeq === currentFrameSeq);
      }

      return allFrameList.findIndex(f => f?.frameSeq === currentFrameSeq);
    })() + 1;

  const onPrevClick = () => (visibleOnlyMyFrame ? prevMine() : prev());
  const onNextClick = () => (visibleOnlyMyFrame ? nextMine() : next());
  const isMoveFrameReadyRef = useRef(false);

  // TODO: 이름 변경
  const unleashIntervalPayloadValue = getVariantPayloadValue<Nullish<boolean>>(
    'vuex.sapporo.interval',
  );
  const isIntervalEnabled = import.meta.env.DEV
    ? true
    : !!unleashIntervalPayloadValue;

  const nextFrameTool = findByName('moveNextFrame')!;
  const prevFrameTool = findByName('movePrevFrame')!;
  const goFirstFrameTool = findByName('moveFirstFrame')!;
  const goLastFrameTool = findByName('moveLastFrame')!;
  const playStopFrameTool = useMemo(
    () => findByName('playStopFrame')!,
    [toolMap],
  );
  const playFrameTool = useMemo(() => findByName('playFrame')!, [toolMap]);
  const stopFrameTool = useMemo(() => findByName('stopFrame')!, [toolMap]);
  const isCES2026Enabled = isEnabled('vuex.sapporo.ces2026CamImagePopup');

  const metaDataItemListForCES2026 = useMemo(() => {
    if (!isCES2026Enabled || !metaData?.position) return [];

    const {
      heading,
      latitude: lat,
      longitude: long,
      speed,
      yawRate: yaw,
    } = metaData.position;

    return [
      {
        Icon: () => <Explore className="size-full" />,
        title: t('labeling.ces2026MetaDataHeading.lowerclassman'),
        value: +heading.toFixed(2),
        valueUnit: '°',
        width: 34,
      },
      {
        Icon: () => <SpeedLine className="size-full" />,
        title: t('labeling.ces2026MetaDataSpeed.embowelment'),
        value: +speed.toFixed(2),
        valueUnit: 'km/h',
        width: 34,
      },
      {
        Icon: () => <Rotate360 className="size-full" />,
        title: t('labeling.ces2026MetaDataYawRate.concinnity'),
        value: +yaw.toFixed(2),
        valueUnit: '°/s',
        width: 34,
      },
      {
        Icon: () => <Latitude className="size-full" />,
        title: t('labeling.ces2026MetaDataLatitude.lutulent'),
        value: +lat.toFixed(2),
        valueUnit: '',
        width: 30,
      },
      {
        Icon: () => <Longitude className="size-full" />,
        title: t('labeling.ces2026MetaDataLongitude.rondawel'),
        value: +long.toFixed(2),
        valueUnit: '',
        width: 34,
      },
    ];
  }, [metaData?.position, isCES2026Enabled]);

  useSubscriptions(
    () => [
      fromEvent(pcdManager, 'afterChangeGeometry')
        .pipe(tap(() => (isMoveFrameReadyRef.current = true)))
        .subscribe(),
    ],
    [pcdIndex],
  );

  useEffect(() => {
    isMoveFrameReadyRef.current = false;
  }, [pcdIndex]);

  useEffect(() => {
    if (!selectedTask) return;

    setPcdInterval(selectedTask.workFrameInterval);
  }, [selectedTask?.id]);

  useEffect(() => {
    prevFrameTool.isSelected && prev();
    nextFrameTool.isSelected && next();
    goFirstFrameTool.isSelected && goFirst();
    goLastFrameTool.isSelected && goLast();
    playFrameTool.isSelected &&
      !isPlaying &&
      play((index, isLast) => {
        !isLast
          ? isMoveFrameReadyRef.current
            ? moveFrameSeq(index + 1)
            : void 0
          : run(playStopFrameTool.command!);
      });
    stopFrameTool.isSelected && isPlaying && stop();
  }, [isPlaying, toolMap]);

  useEffect(() => {
    register(prevFrameTool.command!, () => select(prevFrameTool.id));
    register(nextFrameTool.command!, () => select(nextFrameTool.id));
    register(goFirstFrameTool.command!, () => select(goFirstFrameTool.id));
    register(goLastFrameTool.command!, () => select(goLastFrameTool.id));
    register(playStopFrameTool.command!, () => {
      if (!isPlaying) {
        selectChildren(playStopFrameTool.id, playFrameTool.id);
      } else {
        selectChildren(playStopFrameTool.id, stopFrameTool.id);
      }
    });

    return () => {
      unregister(nextFrameTool.command!);
      unregister(prevFrameTool.command!);
      unregister(playFrameTool.command!);
      unregister(stopFrameTool.command!);
    };
  }, [namespace, isPlaying]);

  useEffect(() => {
    if (!$toolPlayController) return;

    const offsetHeight = $toolPlayController.current?.offsetHeight ?? 0;
    setPosition({
      bottom: isPlaybarOpen ? offsetHeight + 10 : 16,
      right: 16,
    });
    setVariant('secondary');

    return () => {
      clear();
    };
  }, [isPlaybarOpen]);

  useEffect(() => {
    const isMyWork = searchParams.get('myWork');

    if (isMyWork) {
      showOnlyMyFrame();
    }
  }, []);

  return (
    <VFlex
      ref={$toolPlayController}
      className={cn('h-[74px] border border-gray-200 bg-white py-1 pr-4', {
        'absolute bottom-0 left-0 w-full': isPlaybarOpen,
        'absolute bottom-0 left-0 w-[346px] rounded-tr-md rounded-br-md':
          !isPlaybarOpen,
      })}
    >
      <VFlex
        role="button"
        className="h-full w-6 min-w-6 py-[10px]"
        justify="center"
        onClick={togglePlaybar}
      >
        {isPlaybarOpen ? (
          <RightPanelOpen className="size-4" />
        ) : (
          <RightPanelClose className="size-4" />
        )}
      </VFlex>
      <VFlex className="size-full" gap={0.5}>
        <VStack className="w-[286px]" gap={0.25}>
          <VFlex gap={0.5} items="center">
            <VFlex
              className="w-[154px] gap-[2px] rounded-[2px] bg-gray-200 p-[2px]"
              items="center"
            >
              <VFlex
                justify="center"
                items="center"
                role="button"
                onClick={showOnlyMyFrame}
                className={cn('h-7 flex-1 rounded-[2px] text-body-md', {
                  'bg-primary-500 text-skyBlue-200': visibleOnlyMyFrame,
                  'bg-white text-gray-500': !visibleOnlyMyFrame,
                })}
              >
                My frames
              </VFlex>
              <VFlex
                justify="center"
                items="center"
                role="button"
                onClick={showAllFrame}
                className={cn('h-7 flex-1 rounded-[2px] text-body-md', {
                  'bg-primary-500 text-skyBlue-200': !visibleOnlyMyFrame,
                  'bg-white text-gray-500': visibleOnlyMyFrame,
                })}
              >
                All frames
              </VFlex>
            </VFlex>

            <VFlex
              gap={0.5}
              className="h-[26px] w-[124px] rounded-[2px] border border-gray-200 px-2 py-1"
              items="center"
            >
              <div className="text-body-md text-gray-500">Total</div>
              <div className="flex-1 translate-y-[-1px]">
                <span className="text-body-md font-semibold text-gray-600">
                  {seq}
                </span>
                <span className="text-body-md text-gray-600">/{total}</span>
              </div>
            </VFlex>
          </VFlex>

          <VFlex gap={0.5} items="center">
            <VFlex className="w-[154px]">
              <VInput
                fluid
                label="no."
                size="sm"
                type="number"
                orientation="horizontal"
                step={pcdInterval}
                min={1}
                max={allFrameListLength}
                value={currentFrameSeq}
                onChange={event => {
                  const newSeq = clamp(
                    +event.currentTarget.value,
                    1,
                    allFrameListLength,
                  );

                  moveFrameSeq(newSeq);
                }}
              />
            </VFlex>

            <VFlex gap={0.25}>
              <div
                aria-disabled={visibleOnlyMyFrame && !currentFrame?.prevFrame}
                className={cn(
                  'p-[5px] aria-disabled:cursor-not-allowed aria-disabled:text-gray-300',
                )}
                role="button"
                onClick={onPrevClick}
              >
                <ArrowBack className="h-4 w-4" />
              </div>

              <VFlex
                className="size-[26px] p-[5px]"
                justify="center"
                items="center"
                role="button"
                onClick={() => run(playStopFrameTool.command!)}
              >
                {isPlaying ? (
                  <PauseCircle className="h-4 w-4 text-primary-500" />
                ) : (
                  <PlayCircle className="h-4 w-4 text-primary-500" />
                )}
              </VFlex>

              <div
                aria-disabled={visibleOnlyMyFrame && !currentFrame?.nextFrame}
                className={cn(
                  'p-[5px] aria-disabled:cursor-not-allowed aria-disabled:text-gray-300',
                )}
                role="button"
                onClick={onNextClick}
              >
                <ArrowForward className="h-4 w-4" />
              </div>
            </VFlex>
          </VFlex>
        </VStack>

        {isPlaybarOpen && (
          <VStack className="h-full flex-1 overflow-hidden pr-6">
            <VFlex
              className="h-[30px] w-full py-[2px]"
              items="center"
              gap={0.5}
            >
              <div className="w-[124px]">
                {isIntervalEnabled && (
                  <VInput
                    size="sm"
                    label="Interval"
                    fluid
                    type="number"
                    orientation="horizontal"
                    value={pcdInterval}
                    min={1}
                    max={allFrameListLength - 1}
                    onChange={event => {
                      setPcdInterval(
                        clamp(
                          +event.currentTarget.value,
                          1,
                          allFrameListLength - 1,
                        ),
                      );
                    }}
                  />
                )}
              </div>

              {visibleOnlyMyFrame && (
                <VFlex className="w-[275px] gap-[2px]">
                  <VFlex
                    className="rounded-[2px] border border-gray-200 px-2 py-1"
                    gap={0.5}
                  >
                    <div className="text-body-md text-gray-600">Submitted</div>
                    <div className="text-body-md text-status-submitted">
                      {frameCount.submitted ?? 0}
                    </div>
                  </VFlex>

                  <VFlex
                    className="rounded-[2px] border border-gray-200 px-2 py-1"
                    gap={0.5}
                  >
                    <div className="text-body-md text-gray-600">Completed</div>
                    <div className="text-body-md text-status-completed">
                      {frameCount.completed ?? 0}
                    </div>
                  </VFlex>

                  <VFlex
                    className="rounded-[2px] border border-gray-200 px-2 py-1"
                    gap={0.5}
                  >
                    <div className="text-body-md text-gray-600">Rejected</div>
                    <div className="text-body-md text-status-rejected">
                      {frameCount.rejected ?? 0}
                    </div>
                  </VFlex>
                </VFlex>
              )}

              {metaDataItemListForCES2026.length > 0 && (
                <VFlex justify="end" className="flex-1">
                  <VFlex
                    className="w-fit rounded-sm border border-gray-200 px-2 py-1 text-body-sm"
                    items="center"
                    gap={0.5}
                  >
                    <div className="text-gray-500">
                      {t('labeling.ces2026MetaDataTitle.piggish')}
                    </div>

                    <VFlex items="center" gap={0.25}>
                      {metaDataItemListForCES2026.map((item, i) => {
                        return (
                          <>
                            <VFlex key={i} items="center" gap={0.25}>
                              <VTooltip content={item.title} placement="top">
                                <div className="size-4 text-gray-500">
                                  <item.Icon />
                                </div>
                              </VTooltip>
                              <VFlex
                                className="w-fit text-black"
                                items="center"
                              >
                                <div
                                  className="text-right"
                                  style={{ width: item.width }}
                                >
                                  {item.value}
                                </div>
                                <div>{item.valueUnit}</div>
                              </VFlex>
                            </VFlex>
                            {i !== metaDataItemListForCES2026.length - 1 && (
                              <div className="h-3 w-px bg-gray-300"></div>
                            )}
                          </>
                        );
                      })}
                    </VFlex>
                  </VFlex>
                </VFlex>
              )}
            </VFlex>

            <TaskLayoutFooterPlaybar />
          </VStack>
        )}
      </VFlex>
    </VFlex>
  );
};

export default TaskLayoutFooter;
